import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import React from 'react';
import { Route, RouteProps } from 'react-router-dom';
import { SentryErrorBox } from '~frontend-common/components/ui';
import { useTranslator } from '~frontend-common/i18n/translator';
import { Box } from '~ui-kit/components/box';
import { LoadingSpinner } from '~ui-kit/components/loading-spinner';
import { userService } from '~web-portal/services';
import { useAuthStore } from '~web-portal/stores';
import { Navbar } from './navbar';

export function PrivateRoute({ component: Component, ...props }: RouteProps) {
  const translator = useTranslator();
  const userId = useAuthStore.use.userId();

  const userQuery = useQuery({
    queryKey: ['user', userId],
    cacheTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!useAuthStore.getState().isLoggedIn()) {
        return null;
      }

      const user = await userService.loadUserProfile();
      if (!isNil(user.language)) {
        await translator.changeTranslatorLanguage(user.language);
      }

      return user;
    },
  });

  return (
    <Route
      exact
      {...props}
      render={(routeProps) => {
        if (userQuery.isLoading) {
          return (
            <Box flexDirection="column" justifyContent="center" alignItems="center" minHeight="95vh">
              <LoadingSpinner />
            </Box>
          );
        }

        if (userQuery.isError) {
          return <SentryErrorBox error={userQuery.error} translator={translator} my={3} />;
        }

        return (
          <>
            <Navbar isLoggedIn />
            <Component {...routeProps} />
          </>
        );
      }}
    />
  );
}
